import { useState, useEffect, useCallback, useRef } from "react";
import type { PriceData } from "../types";
import { wsUrl } from "../lib/apiBase";

interface UsePriceStreamResult {
	price: PriceData | null;
	connected: boolean;
	error: string | null;
}

/**
 * Custom hook for WebSocket price streaming
 */
export function usePriceStream(symbol: string): UsePriceStreamResult {
	const [price, setPrice] = useState<PriceData | null>(null);
	const [connected, setConnected] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const wsRef = useRef<WebSocket | null>(null);
	const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const connect = useCallback(() => {
		if (wsRef.current?.readyState === WebSocket.OPEN) {
			wsRef.current.close();
		}

		// The backend's own name in production (API_BASE), this page's host in development.
		const ws = new WebSocket(wsUrl("/ws/prices"));
		wsRef.current = ws;

		ws.onopen = () => {
			setConnected(true);
			setError(null);

			// Subscribe to the symbol
			ws.send(JSON.stringify({ type: "subscribe", symbol }));
		};

		ws.onmessage = (event) => {
			try {
				const data = JSON.parse(event.data);

				if (data.type === "price" && data.data) {
					// Verify symbol matches to prevent cross-talk
					if (data.data.symbol === symbol) {
						setPrice(data.data);
					}
				}
			} catch (e) {
				console.error("Failed to parse WebSocket message:", e);
			}
		};

		ws.onerror = () => {
			setError("WebSocket connection error");
			setConnected(false);
		};

		ws.onclose = () => {
			setConnected(false);

			// Attempt to reconnect after 3 seconds
			reconnectTimeoutRef.current = setTimeout(() => {
				if (symbol) {
					connect();
				}
			}, 3000);
		};
	}, [symbol]);

	useEffect(() => {
		if (symbol) {
			connect();
		}

		return () => {
			if (reconnectTimeoutRef.current) {
				clearTimeout(reconnectTimeoutRef.current);
			}
			if (wsRef.current) {
				wsRef.current.close();
			}
		};
	}, [symbol, connect]);

	// Re-subscribe when symbol changes
	useEffect(() => {
		if (wsRef.current?.readyState === WebSocket.OPEN && symbol) {
			wsRef.current.send(JSON.stringify({ type: "subscribe", symbol }));
		}
	}, [symbol]);

	return { price, connected, error };
}
